import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Pill } from "./Pill";
import {
  BUDGET_OPTIONS,
  TIMELINE_OPTIONS,
  INDUSTRY_OPTIONS,
  PLATFORM_OPTIONS,
  PREFERRED_CONTACT_OPTIONS,
  slugifyOption,
} from "./contactOptions";

const TextField = ({ id, label, type = "text", value, onChange, testId, error, required = false }) => (
  <div>
    <Label htmlFor={id} className="text-xs font-semibold text-slate-700">
      {label}{required && " *"}
    </Label>
    <Input
      id={id}
      type={type}
      data-testid={testId}
      value={value}
      onChange={onChange}
      className="mt-1 h-11 rounded-lg border-slate-200 focus-visible:ring-blue-600"
    />
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

const SelectField = ({ id, label, value, onChange, testId, options, placeholder }) => (
  <div>
    <Label htmlFor={id} className="text-xs font-semibold text-slate-700">{label}</Label>
    <select
      id={id}
      data-testid={testId}
      value={value}
      onChange={onChange}
      className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600"
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  </div>
);

const PillGroup = ({ label, testId, options, isActive, onToggle, idPrefix }) => (
  <div>
    <Label className="text-xs font-semibold text-slate-700">{label}</Label>
    <div className="mt-2 flex flex-wrap gap-2" data-testid={testId}>
      {options.map((opt) => (
        <Pill
          key={opt}
          active={isActive(opt)}
          onClick={() => onToggle(opt)}
          testId={`${idPrefix}-${slugifyOption(opt)}`}
        >
          {opt}
        </Pill>
      ))}
    </div>
  </div>
);

export const ContactFormFields = ({ form, errors, loading, setField, toggleArrayField }) => {
  const { t } = useTranslation();

  return (
    <>
      <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">{t("contact.formTitle")}</h2>
      <p className="mt-2 text-sm text-slate-600">{t("contact.formHelp")}</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <TextField id="name" label="Full Name" required value={form.name} onChange={(e) => setField("name", e.target.value)} testId="professional-inquiry-name-input" error={errors.name} />
        <TextField id="email" label="Email Address" type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} testId="professional-inquiry-email-input" error={errors.email} />
        <TextField id="company" label="Company" required value={form.company} onChange={(e) => setField("company", e.target.value)} testId="professional-inquiry-company-input" error={errors.company} />
        <TextField id="role" label="Your Role" required value={form.role} onChange={(e) => setField("role", e.target.value)} testId="professional-inquiry-role-input" error={errors.role} />
        <TextField id="phone" label="Phone Number" value={form.phone} onChange={(e) => setField("phone", e.target.value)} testId="professional-inquiry-phone-input" />
        <div>
          <Label className="text-xs font-semibold text-slate-700">Preferred Contact Method</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="preferred-contact-group">
            {PREFERRED_CONTACT_OPTIONS.map((opt) => (
              <Pill
                key={opt}
                active={form.preferred_contact === opt}
                onClick={() => setField("preferred_contact", opt)}
                testId={`preferred-contact-${opt.toLowerCase()}`}
              >
                {opt}
              </Pill>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <SelectField id="budget" label="Project Budget" value={form.project_budget} onChange={(e) => setField("project_budget", e.target.value)} testId="project-budget-select" options={BUDGET_OPTIONS} placeholder="Select budget range" />
        <SelectField id="timeline" label="Project Timeline" value={form.project_timeline} onChange={(e) => setField("project_timeline", e.target.value)} testId="project-timeline-select" options={TIMELINE_OPTIONS} placeholder="Select timeline" />
      </div>

      <div className="mt-5">
        <PillGroup
          label="Industry Verticals"
          testId="industry-verticals-group"
          options={INDUSTRY_OPTIONS}
          isActive={(opt) => form.industries.includes(opt)}
          onToggle={(opt) => toggleArrayField("industries", opt)}
          idPrefix="industry"
        />
      </div>

      <div className="mt-5">
        <PillGroup
          label="Advertising Platforms"
          testId="advertising-platforms-group"
          options={PLATFORM_OPTIONS}
          isActive={(opt) => form.platforms.includes(opt)}
          onToggle={(opt) => toggleArrayField("platforms", opt)}
          idPrefix="platform"
        />
      </div>

      <div className="mt-5">
        <Label htmlFor="message" className="text-xs font-semibold text-slate-700">Message *</Label>
        <Textarea
          id="message"
          data-testid="professional-inquiry-message-textarea"
          value={form.message}
          onChange={(e) => setField("message", e.target.value)}
          placeholder={t("contact.placeholders.message")}
          rows={5}
          className="mt-1 rounded-lg border-slate-200 focus-visible:ring-blue-600"
        />
        {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-3">
        <Button
          type="submit"
          data-testid="professional-inquiry-submit-button"
          disabled={loading}
          className="h-11 rounded-lg bg-blue-600 px-5 text-white hover:bg-blue-700 disabled:opacity-70"
        >
          {loading ? t("contact.submitting") : (<>Submit Inquiry<ArrowRight className="ml-1.5 h-4 w-4" /></>)}
        </Button>
        <span className="text-xs text-slate-500">* Required fields</span>
      </div>
    </>
  );
};
